import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, ZoomIn, X } from "lucide-react";
import TextReveal from "./animations/TextReveal";

const galleryImages = [
  { id: 1,  src: "/asset/gallery/g1 (1).jpg",  title: "Nvidia Certification" },
  { id: 2,  src: "/asset/gallery/g1 (1).png",  title: "Industrial Visit to CRNS" },
  { id: 3,  src: "/asset/gallery/g1 (2).jpg",  title: "6TH Anniversary" },
  { id: 4,  src: "/asset/gallery/g1 (2).png",  title: "XTreme Session" },
  { id: 5,  src: "/asset/gallery/g1 (3).jpg",  title: "TSYP13 Awards" },
  { id: 6,  src: "/asset/gallery/g1 (4).png",  title: "StepIn IEEE Volunteering 2026" },
  { id: 7,  src: "/asset/gallery/g1 (5).png",  title: "Code of Ethics Session" },
  { id: 8,  src: "/asset/gallery/g1 (5).jpg",  title: "TSYP13" },
  { id: 9,  src: "/asset/gallery/g1 (6).jpg",  title: "TRC2.0" },
  { id: 10, src: "/asset/gallery/g1 (7).jpg",  title: "IEEE Day" },
  { id: 11, src: "/asset/gallery/g1 (8).jpg",  title: "IEEE XTreme 18.0" },
];

const AUTOPLAY_MS = 4000;
const pad = (n) => String(n).padStart(2, "0");

export default function GalleryCarousel() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const closeRef = useRef(null);
  const listRef = useRef(null);
  const stripRef = useRef(null);
  const isInView = useInView(sectionRef, { margin: "-10%" });

  const total = galleryImages.length;
  const current = galleryImages[active];

  const go = useCallback((dir) => setActive((p) => (p + dir + total) % total), [total]);

  // Auto-advance: only while visible, not hovered, lightbox closed
  useEffect(() => {
    if (reduce || paused || zoomed || !isInView) return;
    const t = setTimeout(() => go(1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [active, paused, zoomed, isInView, reduce, go]);

  // Keep the active thumb visible INSIDE its own list (never scrolls the page)
  useEffect(() => {
    const center = (box, axis) => {
      const el = box?.querySelector('[aria-current="true"]');
      if (!box || !el) return;
      if (axis === "y") {
        box.scrollTo({ top: el.offsetTop - box.clientHeight / 2 + el.clientHeight / 2, behavior: reduce ? "auto" : "smooth" });
      } else {
        box.scrollTo({ left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2, behavior: reduce ? "auto" : "smooth" });
      }
    };
    center(listRef.current, "y");
    center(stripRef.current, "x");
  }, [active, reduce]);

  // Lightbox: Esc closes, arrows navigate, page scroll locked while open
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomed(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoomed, go]);

  const swipe = (_, info) => {
    if (info.offset.x < -60) go(1);
    else if (info.offset.x > 60) go(-1);
  };

  const navBtn =
    "w-11 h-11 bg-white/10 hover:bg-white/25 text-white border border-white/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

  return (
    <section ref={sectionRef} className="relative w-full bg-[#f4f7f9] py-16 md:py-24 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg,rgba(15,105,157,0.03) 0px,rgba(15,105,157,0.03) 1px,transparent 1px,transparent 18px),
            repeating-linear-gradient(45deg,rgba(15,105,157,0.02) 0px,rgba(15,105,157,0.02) 1px,transparent 1px,transparent 28px)`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10">
        {/* Header: big outlined count + title */}
        <div className="flex items-end gap-5 mb-10 md:mb-14">
          <span
            className="text-[84px] sm:text-[110px] leading-[0.8] font-black tracking-tighter text-transparent select-none"
            style={{ WebkitTextStroke: "2px #006699" }}
            aria-hidden="true"
          >
            {pad(total)}
          </span>
          <div className="pb-1">
            <TextReveal
              as="h2"
              className="text-sm font-semibold text-[#006699] mb-1 border-l-[3px] border-[#006699] pl-3"
            >
              Our Moments
            </TextReveal>
            <TextReveal
              as="h3"
              className="text-3xl md:text-5xl font-black text-[#00334d] leading-tight tracking-tight"
              stagger={0.06}
            >
              Through the Lens
            </TextReveal>
          </div>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* ── Spotlight ── */}
          <div className="lg:col-span-8 min-w-0" aria-live="polite">
            <div className="relative bg-[#00334d] overflow-hidden aspect-[4/3] sm:aspect-[16/10]">
              {/* blueprint grid */}
              <div
                className="absolute inset-0 opacity-[0.12] pointer-events-none"
                style={{
                  backgroundImage:
                    "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />

              {/* blurred copy of the photo fills the frame whatever its ratio */}
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={`bg-${current.id}`}
                  src={current.src}
                  alt=""
                  aria-hidden="true"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.35 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125"
                />
              </AnimatePresence>

              {/* giant outlined index */}
              <span
                className="absolute -bottom-4 -right-2 text-[130px] sm:text-[200px] leading-none font-black tracking-tighter text-transparent select-none pointer-events-none tabular-nums z-[1]"
                style={{ WebkitTextStroke: "1.5px rgba(124,196,232,0.35)" }}
                aria-hidden="true"
              >
                {pad(active + 1)}
              </span>

              <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#7cc4e8] z-20 pointer-events-none" />
              <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#7cc4e8] z-20 pointer-events-none" />

              {/* zoom */}
              <button
                onClick={() => setZoomed(true)}
                aria-label="Open photo in large view"
                className="absolute top-3 right-3 z-30 w-10 h-10 bg-[#00334d]/80 hover:bg-[#006699] text-white border border-white/25 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7cc4e8]"
              >
                <ZoomIn className="w-4 h-4" />
              </button>

              {/* arrows on the image */}
              <button onClick={() => go(-1)} aria-label="Previous photo" className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-10 sm:h-10 bg-[#00334d]/80 hover:bg-[#006699] text-white border border-white/20 flex items-center justify-center transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => go(1)} aria-label="Next photo" className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-10 sm:h-10 bg-[#00334d]/80 hover:bg-[#006699] text-white border border-white/20 flex items-center justify-center transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* photo (swipe on touch, click to zoom) */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={reduce ? false : { opacity: 0, scale: 0.97, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.25}
                  onDragEnd={swipe}
                  style={{ touchAction: "pan-y" }}
                  className="absolute inset-0 flex items-center justify-center px-14 py-6 sm:px-16 sm:py-8 pb-20 z-10"
                >
                  <img
                    src={current.src}
                    alt={current.title}
                    onClick={() => setZoomed(true)}
                    draggable={false}
                    className="max-w-full max-h-full object-contain cursor-zoom-in shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-white/20"
                  />
                </motion.div>
              </AnimatePresence>

              {/* caption */}
              <div className="absolute inset-x-0 bottom-0 z-20 px-5 sm:px-8 py-4 bg-gradient-to-t from-[#00334d] via-[#00334d]/80 to-transparent pointer-events-none">
                <p className="text-xs font-semibold text-[#7dd3fc] tabular-nums">
                  {active + 1} of {total}
                </p>
                <h4 className="mt-0.5 text-lg sm:text-2xl font-black text-white leading-tight truncate">
                  {current.title}
                </h4>
              </div>

              {/* autoplay progress */}
              {!reduce && (
                <div className="absolute inset-x-0 bottom-0 h-[3px] bg-white/10 z-30">
                  <motion.div
                    key={`${active}-${paused}`}
                    className="h-full bg-[#7dd3fc]"
                    initial={{ width: "0%" }}
                    animate={{ width: paused || zoomed ? "0%" : "100%" }}
                    transition={{ duration: paused || zoomed ? 0 : AUTOPLAY_MS / 1000, ease: "linear" }}
                  />
                </div>
              )}
            </div>

            {/* phone / tablet: thumbnail strip under the photo */}
            <div className="lg:hidden bg-white border border-[#006699]/10 border-t-0">
              <div
                ref={stripRef}
                className="relative flex gap-2 overflow-x-auto snap-x snap-mandatory px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                aria-label="All photos"
              >
                {galleryImages.map((img, i) => {
                  const on = i === active;
                  return (
                    <button
                      key={img.id}
                      onClick={() => setActive(i)}
                      aria-current={on}
                      aria-label={img.title}
                      className={`snap-center flex-shrink-0 w-[72px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] transition-opacity ${on ? "opacity-100" : "opacity-50"}`}
                    >
                      <img src={img.src} alt="" loading="lazy" className={`w-full h-14 object-cover ${on ? "ring-2 ring-[#006699]" : ""}`} />
                      <span className={`block text-[11px] mt-1 font-bold tabular-nums ${on ? "text-[#006699]" : "text-[#00334d]/60"}`}>
                        {pad(i + 1)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="px-4 pb-3 -mt-1 text-[11px] text-slate-400">Swipe the photo or tap a thumbnail</p>
            </div>
          </div>

          {/* ── Index (desktop): scrolls inside, never taller than the photo ── */}
          <nav className="hidden lg:block lg:col-span-4 relative" aria-label="All photos">
            <div
              ref={listRef}
              className="absolute inset-0 overflow-y-auto overscroll-contain border-t border-[#006699]/15 [scrollbar-width:thin]"
            >
              <ul>
                {galleryImages.map((img, i) => {
                  const on = i === active;
                  return (
                    <li key={img.id}>
                      <button
                        onClick={() => setActive(i)}
                        aria-current={on}
                        className={`group relative w-full text-left flex items-center gap-4 py-3 pl-5 pr-3 border-b border-[#006699]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#006699] ${
                          on ? "bg-[#006699]/[0.06]" : "hover:bg-[#006699]/[0.03]"
                        }`}
                      >
                        <span className={`absolute left-0 top-0 bottom-0 w-[3px] bg-[#006699] origin-top transition-transform duration-300 ${on ? "scale-y-100" : "scale-y-0 group-hover:scale-y-50"}`} />
                        <span className={`text-xs font-black tabular-nums w-6 flex-shrink-0 ${on ? "text-[#006699]" : "text-[#00334d]/30"}`}>
                          {pad(i + 1)}
                        </span>
                        <img
                          src={img.src}
                          alt=""
                          loading="lazy"
                          className={`w-16 h-12 object-cover flex-shrink-0 transition-all duration-300 ${on ? "opacity-100 ring-1 ring-[#006699]" : "opacity-60 grayscale group-hover:opacity-100 group-hover:grayscale-0"}`}
                        />
                        <span className={`flex-1 min-w-0 text-sm leading-snug transition-colors ${on ? "font-bold text-[#00334d]" : "font-semibold text-[#00334d]/75"}`}>
                          {img.title}
                        </span>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${on ? "text-[#006699] opacity-100" : "text-[#006699]/40 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>
      </div>

      {/* ───────── Lightbox: fixed to the screen, same as Awards & Events ───────── */}
      <AnimatePresence>
        {zoomed && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={current.title}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={() => setZoomed(false)}
          >
            <button ref={closeRef} onClick={() => setZoomed(false)} aria-label="Close" className={`${navBtn} absolute top-4 right-4 sm:top-6 sm:right-6 z-10`}>
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              aria-label="Previous photo"
              className={`${navBtn} absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              aria-label="Next photo"
              className={`${navBtn} absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center max-w-5xl w-full px-10 sm:px-16" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={current.id}
                  src={current.src}
                  alt={current.title}
                  initial={reduce ? false : { opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.25}
                  onDragEnd={swipe}
                  draggable={false}
                  style={{ touchAction: "pan-y" }}
                  className="max-w-full max-h-[75vh] object-contain bg-white shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                />
              </AnimatePresence>
              <p className="mt-4 text-sm font-semibold text-white text-center leading-snug">{current.title}</p>
              <p className="mt-1 text-xs text-white/70 tabular-nums">{active + 1} of {total}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}